import { ServerSearchCombobox } from '@/components/ServerSearchCombobox';

// ─── EstimateCombobox ───────────────────────────────────────────────────────────
// A select-only typeahead over GET /api/estimates?q=. A thin adapter over
// ServerSearchCombobox: it just supplies the endpoint, row shape, and rendering,
// and maps the chosen row to the minimal EstimatePick handed to the parent.

/** The minimal shape handed back to the parent on selection. */
export interface EstimatePick {
  id: number;
  number: string;
  customer_name: string;
}

interface EstimateSearchRow {
  id: number;
  number: string;
  customer_name: string;
  date: string;
  status: string;
  total: number;
  amount_paid: number;
}

interface EstimatesSearchResponse {
  data: { estimates: EstimateSearchRow[] };
}

interface EstimateComboboxProps {
  /** Selected estimate id, or null. Controlled by the parent (drives input styling). */
  value: number | null;
  /** Called with the chosen estimate, or null when the selection is cleared by typing. */
  onChange: (pick: EstimatePick | null) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function EstimateCombobox({
  value,
  onChange,
  disabled,
  placeholder,
}: EstimateComboboxProps) {
  return (
    <ServerSearchCombobox<EstimateSearchRow>
      value={value}
      onChange={(row) =>
        onChange(row ? { id: row.id, number: row.number, customer_name: row.customer_name } : null)
      }
      queryKey="estimate-picker"
      buildUrl={(q) => `/api/estimates?q=${encodeURIComponent(q)}`}
      extract={(resp) => (resp as EstimatesSearchResponse | undefined)?.data.estimates ?? []}
      getKey={(e) => e.id}
      getLabel={(e) => `${e.number} · ${e.customer_name}`}
      renderResult={(e) => (
        <>
          <span className="font-mono font-medium shrink-0">{e.number}</span>
          <span className="text-muted-foreground truncate">· {e.customer_name}</span>
        </>
      )}
      disabled={disabled}
      placeholder={placeholder ?? 'Search estimates…'}
      promptMessage="Type to search estimates"
      emptyMessage="No matching estimates"
    />
  );
}
